import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const AccurateResults = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    {
      number: '01',
      title: 'Seamless Sensor Integration',
      description: 'CIVIONIKS supports a wide range of sensors — wireless, tilt, vibration, corrosion, and more — through a simple, plug-and-play API.',
      icon: (
        <svg className="w-6 h-6 xs:w-8 xs:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      number: '02',
      title: 'Built for Sensor Manufacturers',
      description: 'Add AI power to your hardware. Sensor makers can integrate with CIVIONIKS using our flexible API layer and instantly deliver AI-powered features to clients.',
      icon: (
        <svg className="w-6 h-6 xs:w-8 xs:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 7.172V5L8 4z" />
        </svg>
      ),
    },
    {
      number: '03',
      title: 'Advanced Visualization',
      description: 'Experience advanced visualization tools that provide comprehensive insights into structural behavior and performance, empowering you to make informed decisions.',
      icon: (
        <svg className="w-6 h-6 xs:w-8 xs:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
  ];

  return (
    <section className="section-padding-mobile bg-gray-50 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -right-12 w-48 h-48 xs:-right-24 xs:w-96 xs:h-96 bg-primary-100/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 -left-12 w-48 h-48 xs:-left-24 xs:w-96 xs:h-96 bg-accent-100/20 rounded-full blur-3xl"></div>
      </div>

      <div className="container-mobile relative">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 xs:mb-16 lg:mb-20"
        >

          <h2 id="accurate-results-heading" className="text-mobile-title font-bold text-gray-900 mb-4 xs:mb-6">
            Accurate and{' '}
            <span className="gradient-text">Comprehensive Results</span>
          </h2>
          <p className="text-mobile-body text-gray-600 max-w-4xl mx-auto">
            Our platform delivers precise, reliable data processing with comprehensive analysis capabilities
          </p>
        </motion.div>

        <div className="space-y-8 xs:space-y-12 lg:space-y-16">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className={`flex flex-col lg:flex-row items-center gap-6 xs:gap-8 lg:gap-16 ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              {/* Content */}
              <div className="flex-1 space-y-4 xs:space-y-6">
                <div className="flex items-center space-x-3 xs:space-x-4">
                  <div className="w-12 h-12 xs:w-16 xs:h-16 bg-primary-600 text-white rounded-full xs:rounded-xl flex items-center justify-center text-lg xs:text-2xl font-bold shadow-mobile-medium xs:shadow-medium">
                    {feature.number}
                  </div>
                  <div className="text-primary-600">
                    {feature.icon}
                  </div>
                </div>
                
                <h3 className="text-xl xs:text-2xl lg:text-3xl font-bold text-gray-900">
                  {feature.title}
                </h3>
                
                <p className="text-base xs:text-lg text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>

              {/* Visual element */}
              <div className="flex-1 flex justify-center w-full">
                <div className="w-full max-w-sm xs:max-w-md">
                  <div className="bg-white rounded-xl xs:rounded-2xl shadow-mobile-large xs:shadow-lg p-4 xs:p-6 lg:p-8 border border-gray-100">
                    <div className="space-y-3 xs:space-y-4">
                      {/* Simulated data visualization */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs xs:text-sm font-medium text-gray-600">Integration Status</span>
                        <span className="text-xs xs:text-sm font-semibold text-green-600">Active</span>
                      </div>
                      
                      <div className="space-y-2 xs:space-y-3">
                        {[85, 92, 78, 96].map((value, idx) => (
                          <div key={idx} className="flex items-center space-x-2 xs:space-x-3">
                            <div className="w-1.5 h-1.5 xs:w-2 xs:h-2 bg-primary-500 rounded-full"></div>
                            <div className="flex-1 bg-gray-200 rounded-full h-1.5 xs:h-2">
                              <div 
                                className="bg-gradient-to-r from-primary-500 to-primary-600 h-1.5 xs:h-2 rounded-full transition-all duration-1000"
                                style={{ width: inView ? `${value}%` : '0%' }}
                              ></div>
                            </div>
                            <span className="text-xs xs:text-sm font-medium text-gray-700">{value}%</span>
                          </div>
                        ))}
                      </div>

                      <div className="pt-3 xs:pt-4 border-t border-gray-200">
                        <div className="text-center">
                          <div className="text-xl xs:text-2xl font-bold text-primary-600">99.2%</div>
                          <div className="text-xs xs:text-sm text-gray-600">Overall Accuracy</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Section divider */}
      <div className="section-divider mt-16 xs:mt-20 lg:mt-24"></div>
    </section>
  );
};

export default AccurateResults; 